"use client";

import Link from "next/link";
import Image from "next/image";
import { MessageCircle } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect, useState } from "react";
import { getRecentDiscussions } from "@/lib/services/commentsService";
import type { RecentDiscussion } from "@/lib/types/comments";

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "только что";
  if (diffMins < 60) return `${diffMins} мин назад`;
  if (diffHours < 24) return `${diffHours} ч назад`;
  if (diffDays === 1) return "вчера";
  return `${diffDays} дн назад`;
}

function formatReplyCount(count: number): string {
  if (count === 0) return "";
  if (count === 1) return "1 ответ";
  if (count >= 2 && count <= 4) return `${count} ответа`;
  return `${count} ответов`;
}

function DiscussionSkeleton() {
  return (
    <div className="space-y-1">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-2 space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-7 w-7 rounded-md shrink-0" />
            <Skeleton className="h-3 w-28" />
          </div>
          <div className="flex items-start gap-2">
            <Skeleton className="h-6 w-6 rounded-full shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-2.5 w-40 mt-1" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function DiscussionsWidget() {
  const [discussions, setDiscussions] = useState<RecentDiscussion[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchDiscussions() {
      const result = await getRecentDiscussions();
      if (result.success && result.data) {
        setDiscussions(result.data.discussions);
      }
      setIsLoading(false);
    }
    fetchDiscussions();
  }, []);

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="h-4 w-4 text-foreground-secondary" />
        <h3 className="text-sm font-semibold text-foreground">
          Обсуждения
        </h3>
      </div>

      {isLoading ? (
        <DiscussionSkeleton />
      ) : discussions.length === 0 ? (
        <p className="text-sm text-foreground-tertiary text-center py-4">
          Пока нет обсуждений
        </p>
      ) : (
        <div className="space-y-1">
          {discussions.map((discussion) => (
            <Link
              key={discussion.id}
              href={`/products/${discussion.productSlug}#comments`}
              className="block -mx-2 px-2 py-2 rounded-lg hover:bg-accent transition-colors"
            >
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {discussion.productLogoUrl ? (
                    <Image
                      src={discussion.productLogoUrl}
                      alt={discussion.productName}
                      width={28}
                      height={28}
                      className="h-7 w-7 rounded-sm object-cover shrink-0"
                    />
                  ) : (
                    <div className="h-7 w-7 rounded-md bg-accent shrink-0" />
                  )}
                  <p className="text-xs font-medium text-foreground-secondary">
                    {discussion.productName}
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Avatar className="h-6 w-6 shrink-0">
                    <AvatarImage src={discussion.author?.avatarUrl ?? undefined} />
                    <AvatarFallback className="text-xs">
                      {(discussion.author?.name ?? "?").substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground-secondary line-clamp-2">
                      {discussion.content}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-foreground-tertiary">
                      <span>{discussion.author?.name ?? "Аноним"}</span>
                      <span>·</span>
                      <span>{formatTimeAgo(discussion.createdAt)}</span>
                      {discussion.replyCount > 0 && (
                        <>
                          <span>·</span>
                          <span>{formatReplyCount(discussion.replyCount)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
